import { useCallback, useRef } from 'react'

import type { PredictionEvent } from '@/services/prediction/types'

import type { ColorMaps, GetTeamColorFn } from '../utils/color'
import { buildEventColorMaps, rescueTeamColor } from '../utils/color'

/**
 * Port of cdc-web `PinnedEvents/hooks/useGetTeamColor.ts`。
 *
 * 差异（已决策）：cdc-market 是 dark-only（无 light 主题），`rescueTeamColor`
 * 的 scheme 硬编码 `'dark'`（亮度钳 ≥ 0.62）。
 *
 * 快照语义：颜色映射按 `event.id` 缓存——同一事件在轮询刷新中数据抖动
 * （合约进出场、价格变动）不会让已展示的胜负色跳变。只在 contractsColorMap
 * 非空时覆盖快照，避免空 enriched 数据把已分配的颜色清空。
 */
export function useGetTeamColor(event: PredictionEvent | undefined): GetTeamColorFn {
  const snapshotRef = useRef<{ id: string | undefined; colorMaps: ColorMaps } | null>(null)

  if (event?.id && snapshotRef.current?.id !== event.id) {
    const colorMaps = buildEventColorMaps(event)
    if (colorMaps.contractsColorMap.size > 0) {
      snapshotRef.current = { id: event.id, colorMaps }
    }
  }

  const colorMaps = snapshotRef.current?.colorMaps

  return useCallback(
    (teamId?: string | null, contractId?: string | null) => {
      if (teamId && colorMaps?.teamsColorMap) {
        return rescueTeamColor(colorMaps.teamsColorMap.get(teamId) ?? null, 'dark')
      }
      if (contractId && colorMaps?.contractsColorMap) {
        return rescueTeamColor(colorMaps.contractsColorMap.get(contractId) ?? null, 'dark')
      }
      return null
    },
    [colorMaps],
  )
}
